import {
  GatewayControleQuinquennal,
  GatewayTicketControleQuinquennal,
} from "@conformite/gateway";
import CheckFilled from "@endya-conformite/ui-kit/src/assets/icons/check-circle-fill.svg?react";
import CheckOutline from "@endya-conformite/ui-kit/src/assets/icons/check-circle-outline.svg?react";
import DotInfo from "@endya-conformite/ui-kit/src/assets/icons/dots-circle-outline.svg?react";
import Error from "@endya-conformite/ui-kit/src/assets/icons/x-circle-fill.svg?react";
import { add } from "date-fns";

export const TicketStatus =
  GatewayTicketControleQuinquennal.TicketControleQuinquennalStatus;

export const TicketAxeStatus =
  GatewayControleQuinquennal.ControleQuinquennalAxeStatus;

type IconComponentType = React.FC<{ color?: string }>;

type TicketStatusConfig = {
  variant: "info" | "success" | "error";
  label: string;
  icon: IconComponentType;
  color: string;
};

const icons: Record<"success" | "info" | "error", IconComponentType> = {
  success: CheckFilled as IconComponentType,
  info: DotInfo as IconComponentType,
  error: Error as IconComponentType,
};

const color: Record<"success" | "info" | "error", string> = {
  success: "#037c5e",
  info: "#0067e6",
  error: "#d43535",
};

export const statusConfig = (
  variant: TicketStatusConfig["variant"],
  label: string
): TicketStatusConfig => ({
  variant,
  label,
  icon: icons[variant],
  color: color[variant],
});

export const TICKET_STATUS_MAP: Record<
  GatewayTicketControleQuinquennal.TicketControleQuinquennalStatus,
  TicketStatusConfig
> = {
  [TicketStatus.FERME]: {
    ...statusConfig("success", "Terminée"),
    icon: CheckOutline as IconComponentType,
  },
  [TicketStatus.EN_COURS]: statusConfig("info", "En cours"),
  [TicketStatus.A_TRAITER]: statusConfig("info", "À traiter"),
  [TicketStatus.PRE_COMMISSION]: statusConfig("info", "Pré-commission"),
  [TicketStatus.A_ASSIGNER]: statusConfig("info", "À assigner"),
  [TicketStatus.EN_REMEDIATION]: statusConfig("error", "En remédiation"),
  [TicketStatus.RADIE]: statusConfig("error", "Radié"),
  [TicketStatus.ANNULE]: statusConfig("error", "Annulé"),
  [TicketStatus.PIECES_COMPLEMENTAIRES]: statusConfig(
    "info",
    "Pièces complémentaires"
  ),
};

const STATUSES_WITHOUT_DATE_BUTOIR = [
  TicketStatus.FERME,
  TicketStatus.PRE_COMMISSION,
  TicketStatus.RADIE,
  TicketStatus.ANNULE,
  TicketStatus.PIECES_COMPLEMENTAIRES,
];

const READ_ONLY_PIPELINE_STAGES: GatewayTicketControleQuinquennal.TicketControleQuinquennalStatus[] =
  [TicketStatus.FERME, TicketStatus.RADIE, TicketStatus.ANNULE];

export const isControleQuinquennalReadOnly = (
  pipelineStage: GatewayTicketControleQuinquennal.TicketControleQuinquennalStatus
) => READ_ONLY_PIPELINE_STAGES.includes(pipelineStage);

export const getControleQuinquennalDateButoir = ({
  dateDebut,
  dateCreation,
  datePremiereRemediation,
  pipelineStage,
}: Pick<
  GatewayControleQuinquennal.ControleQuinquennalAdminResponse,
  "dateDebut" | "dateCreation" | "datePremiereRemediation" | "pipelineStage"
>): Date | null => {
  if (STATUSES_WITHOUT_DATE_BUTOIR.includes(pipelineStage)) {
    return null;
  }
  const dateRef = dateDebut ?? dateCreation;
  return datePremiereRemediation
    ? add(new Date(datePremiereRemediation), { months: 2 })
    : add(new Date(dateRef), { days: 21 });
};

export const AXE_STATUS_MAP: Record<
  GatewayControleQuinquennal.ControleQuinquennalAxeStatus,
  TicketStatusConfig
> = {
  [TicketAxeStatus.EN_COURS]: statusConfig("info", "À contrôler"),
  [TicketAxeStatus.REMEDIE]: statusConfig("error", "En remédiation"),
  [TicketAxeStatus.VALIDE]: statusConfig("success", "Contrôle validé"),
};

export const { VerificationStatus } = GatewayControleQuinquennal;

// Statut du traitement IA d'un axe (relance/suivi, écran vérificateur recette).
export const VERIFICATION_STATUS_MAP: Record<
  Exclude<
    GatewayControleQuinquennal.VerificationStatus,
    typeof GatewayControleQuinquennal.VerificationStatus.SUCCESS
  >,
  TicketStatusConfig
> = {
  [VerificationStatus.PENDING]: statusConfig("info", "Analyse en cours"),
  [VerificationStatus.FAILED]: statusConfig("error", "Analyse échouée"),
};
